import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { routeTitleMap } from 'apps/app/src/assets/titles/routing-title.record';

@Injectable({
  providedIn: 'root',
})
export class TitleService {
  public constructor(private title: Title) {}

  private _currentTitle = 'AG2 Trader';
  private _originalTitle = 'AG2 Trader';
  private placeholderTimeout: ReturnType<typeof setTimeout> | null;

  /**
   * Sets the page title based on the current URL.
   * Titles can contain placeholders in the format {placeholderName} which will be
   * replaced with actual values via setParametersForTheTitle().
   * Unreplaced placeholders are automatically stripped after 50ms.
   *
   * @param url The current route URL to match against routeTitleMap
   *
   * @example
   * // In routing guard or component initialization:
   * this.titleService.setCurrentTitle('/live-monitor/123');
   * // This will set title to 'Live Monitor - {dashboardName}' if found in routeTitleMap
   */
  public setCurrentTitle(url: string) {
    // Clear any pending timeout from previous calls
    if (this.placeholderTimeout) {
      clearTimeout(this.placeholderTimeout);
    }

    this._originalTitle = this._findTitleByUrlPrefix(url);
    this._currentTitle = this._originalTitle;

    // Set title with placeholders stripped, but allow them to be replaced soon
    this._updateTitleWithTimeout();
  }

  /**
   * Replaces placeholders in the current title with actual values.
   * Placeholders are defined using curly braces, e.g., {dashboardName}.
   * Any placeholders not provided in the params object will remain as-is.
   *
   * @param params Object containing key-value pairs to replace placeholders.
   *               Keys should match placeholder names without curly braces.
   *
   * @example
   * // After calling setCurrentTitle('/live-monitor/123'):
   * this.titleService.setParametersForTheTitle({ dashboardName: 'My Dashboard' });
   * // Title will update to 'Live Monitor - My Dashboard'
   *
   * @example
   * // Multiple parameters:
   * this.titleService.setParametersForTheTitle({
   *   dashboardName: 'Sales Report',
   *   region: 'North America'
   * });
   */
  public setParametersForTheTitle(params: Record<string, string>) {
    if (!this._originalTitle) return;

    // Clear any pending timeout
    if (this.placeholderTimeout) {
      clearTimeout(this.placeholderTimeout);
      this.placeholderTimeout = null;
    }

    // Use the original template for replacement
    this._currentTitle = this._originalTitle.replace(/\{(\w+)\}/g, (_, key) => {
      return key in params ? params[key] : `{${key}}`;
    });

    this.title.setTitle(this._currentTitle);
  }

  private _updateTitleWithTimeout() {
    // Strip placeholders immediately to prevent showing placeholder text
    const strippedTitle = this._currentTitle.replace(/\{(\w+)\}/g, '');
    this.title.setTitle(strippedTitle);

    // Allow 50ms for setParametersForTheTitle to be called and update with actual values
    // If called, it will clear this timeout and set the title with replaced parameters
    this.placeholderTimeout = setTimeout(() => {
      this.placeholderTimeout = null;
    }, 50);
  }

  private _findTitleByUrlPrefix(url: string): string {
    let bestMatch = '';

    for (const key in routeTitleMap) {
      if (url.startsWith(key) && key.length > bestMatch.length) {
        bestMatch = key;
      }
    }

    return bestMatch ? routeTitleMap[bestMatch] : 'AG2 Trader';
  }
}
